/**
 *  Description: To Add Parameter within the PcParam table when "Add Parameter" button is clicked
 *  Date: 23/5/2025
 */

import React, { useState } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Select,
} from "@mantine/core";
import classes from "./add.module.css";
import { RowDataPCComponentParameter } from "./types";

enum CpuType {
  AMD = 'AMD',
  INTEL = 'INTEL'
}

enum Category {
  cpuChipset = 'cpuChipset',
  cpuSocket = 'cpuSocket',
  ramDDR = 'ramDDR',
  coolerHeatExchangeMedium = 'coolerHeatExchangeMedium',
  coolerType = 'coolerType',
  psuFormFactor = 'psuFormFactor',
  psu80PlusStandard = 'psu80PlusStandard',
  ssdM2Type = 'ssdM2Type'
}

interface AddParameterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddParameter: (newParameterData: RowDataPCComponentParameter) => void;
}

const AddParameterModal: React.FC<AddParameterModalProps> = ({
  isOpen,
  onClose,
  onAddParameter,
}) => {
  const [newParameter, setNewParameter] = useState<RowDataPCComponentParameter>({
    id: "",
    category: "",
    value: "",
    label: "",
    type: "",
  });

  const handleAddParameter = () => {

    // Check if any required fields are empty
    if (!newParameter.category || !newParameter.value || !newParameter.label ) {
      alert("Please fill in all the required fields");
      return;
    }

    if (newParameter.category === "cpuChipset" || newParameter.category === "cpuSocket") {
      if (newParameter.type !== CpuType.AMD && newParameter.type !== CpuType.INTEL){
        alert("Please select INTEL or AMD");
        return;
      }
    }

    onAddParameter(newParameter);

    // Clear inputs and reset state
    setNewParameter({
    id: "",
    category: "",
    value: "",
    label: "",
    type: "",
    });

    onClose();
  };

  const handleClose = () => {
    // Clear inputs and reset state
    setNewParameter({
    id: "",
    category: "",
    value: "",
    label: "",
    type: "",
    });

    // Call the onClose function
    onClose();
  };

  return (
    <Modal
      style={{ zIndex: "100" }}
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          New Parameter
        </div>
      }
      opened={isOpen}
      onClose={handleClose}
    >

    <Select
      label="Category"
      placeholder="Select Category"
      required
      data={[
        { value: Category.cpuChipset, label: Category.cpuChipset },
        { value: Category.cpuSocket, label: Category.cpuSocket },
        { value: Category.ramDDR, label: Category.ramDDR },
        { value: Category.coolerHeatExchangeMedium, label: Category.coolerHeatExchangeMedium },
        { value: Category.coolerType, label: Category.coolerType },
        { value: Category.psuFormFactor, label: Category.psuFormFactor },
        { value: Category.psu80PlusStandard, label: Category.psu80PlusStandard },
        { value: Category.ssdM2Type, label: Category.ssdM2Type },
      ]}
      onChange={(value) => {
        setNewParameter({
          ...newParameter, category: value, type: 'NONE'
        });
      }}
    />

    <Select
      label="Brand Type"
      placeholder="Not Applicable"
      description="Use INTEL or AMD only for 'cpuChipset' or 'cpuSocket'."
      required
      my="sm"
      data={[
        { value: 'NONE', label: 'Not Applicable' },
        { value: CpuType.INTEL, label: CpuType.INTEL },
        { value: CpuType.AMD, label: CpuType.AMD },
      ]}
      value={newParameter.type ?? ''}
      disabled={
        newParameter.category !== 'cpuSocket' &&
        newParameter.category !== 'cpuChipset'
      }
      onChange={(value) => {
        setNewParameter({ ...newParameter, type: value });
      }}
    />

      <TextInput
        label="Parameter Name"
        required
        placeholder="Input Parameter"
        maxLength={255}
        value={newParameter.label}
        onChange={(event) =>
          setNewParameter({ ...newParameter, 
            label: event.currentTarget.value, 
            value: event.currentTarget.value })
        }
      />

      <Group className={classes.buttonBottom}>
        <Button onClick={() => onClose()} className={classes.buttonBack}>
          Back
        </Button>
        <Button onClick={handleAddParameter} className={classes.buttonSubmit}>
          Create
        </Button>
      </Group>
    </Modal>
  );
};

export default AddParameterModal;
